import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ProgramPrintCopy } from '../components/program/ProgramPrint';

const API = process.env.REACT_APP_API_URL || '';

// Loads what the print copy shows. Without a key: the public program, people and event of the slug
// (what the event page shows). With ?preview={key} (the admin's PDF preview, also of a draft): the same
// three through publicapi.ProgramPrintPreviewController, which only answers while the key is valid.
// status: 'loading', 'ready' or 'none' (nothing to print: unknown, unpublished, an expired key).
const usePrintData = (slug, previewKey) => {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    const base = previewKey
      ? `${API}/api/public/program-print-preview/${encodeURIComponent(previewKey)}`
      : `${API}/api/public/events/${encodeURIComponent(slug)}`;
    const eventCall = previewKey
      ? axios.get(`${base}/event`).then((r) => ({ de: r.data?.nameDe, en: r.data?.nameEn }))
      : axios.get(`${API}/api/cms/event/public/get-by-slug/${encodeURIComponent(slug)}`).then((r) => ({
        de: r.data?.mainHeadline || r.data?.bigHeadline,
        en: r.data?.mainHeadlineEN || r.data?.bigHeadlineEN,
      }));
    Promise.all([
      axios.get(previewKey ? base : `${base}/program`).then((r) => r.data),
      axios.get(`${base}/people`).then((r) => r.data).catch(() => null),
      eventCall.catch(() => null),
    ])
      .then(([program, people, names]) => {
        if (cancelled) return;
        const printable = (Array.isArray(program?.entries) && program.entries.length > 0) || !!program?.programPdf?.url;
        setState(printable ? { status: 'ready', program, people, names } : { status: 'none' });
      })
      .catch(() => { if (!cancelled) setState({ status: 'none' }); });
    return () => { cancelled = true; };
  }, [slug, previewKey]);

  return state;
};

// /events/:slug/program/print?lang=de|en[&preview={key}] - the program of an event as its print copy.
// The server's PDF renderer (heartscms programimport.GeneratedProgramPdf, through Gotenberg's Chromium)
// opens this page, waits until window.programPrintReady is true and prints it: the public program PDF
// in both languages, and the admin's PDF preview with a key. Nothing to print leaves
// window.programPrintReady at "none". A person opening it sees an empty page on screen: the copy is
// for print media only (ProgramPrint's styles).
const ProgramPrintPage = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { language, setLanguage } = useLanguage();
  const wanted = params.get('lang') === 'en' ? 'en' : 'de';
  const { status, program, people, names } = usePrintData(slug, params.get('preview'));

  useEffect(() => {
    if (language !== wanted) setLanguage(wanted);
  }, [language, wanted, setLanguage]);

  useEffect(() => {
    if (status === 'loading') window.programPrintReady = false;
    if (status === 'none') window.programPrintReady = 'none';
  }, [status]);

  const ready = useCallback(() => { window.programPrintReady = true; }, []);

  if (status !== 'ready' || language !== wanted) return null;
  const title = (wanted === 'en' ? names?.en || names?.de : names?.de || names?.en) || undefined;
  if (title) document.title = title;
  return <ProgramPrintCopy program={program} people={people} language={wanted} title={title} onReady={ready} />;
};

export default ProgramPrintPage;
